import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import GoBackToPrev from '../../GoBackToPrev';
import StyledActiveButton from 'components/Button/StyledActiveButton';
import { MainWrapper } from '../style';
import { requestApi } from 'utils/request';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { COMMON_ERR_MESSAGE } from '../../../../constants/message';
import { useUI } from '../../../../contexts/UIContext';

const FillQRFormStep = ({
  mode,
  selectedMallData,
  setStep,
  formData,
  setFormData,
  setShowModal,
  setModalData,
  setCaptchaData,
  dataProviderSeq,
}) => {
  const location = useLocation();
  const { setContextLoading } = useUI();
  // QR 신규 등록 UI 노출
  const [isNewQR, setIsNewQR] = useState(mode !== 'modify');
  const [QRData, setQRData] = useState({});

  // 데이터 연동 관리 > QR 에러 발생(FRT125) 으로 진입한 케이스
  const isDirectQRStep = location?.state?.step === 'QRStep';

  const { qrFileSeq, qrFileUrl, otpSerialNumber } = QRData;
  const { mallSeq } = selectedMallData;

  // 이전으로 돌아가기 버튼 클릭
  const onClickBackBtn = () => {
    // 캡처 데이터 초기화
    setCaptchaData({});
    // 판매몰 계정 입력으로 이동
    setStep('accountStep');
  };

  // 이미지 변경 및 업로드
  const onChangeImageFile = async event => {
    const file = event.target.files[0];
    const [fileType, fileExtension] = file['type'].split('/');
    // 방어코드: 동일한 파일 선택 시, 인식 가능
    event.target.value = '';
    // 이미지 파일 타입 검증
    if (
      fileType !== 'image' ||
      !['jpg', 'png', 'jpeg'].includes(fileExtension)
    ) {
      setModalData({ contents: '이미지는 JPG, PNG, JPEG만 등록 가능합니다.' });
      setShowModal(true);
      return;
    }
    await fetchUploadFile(file);
  };

  // otp 숫자 입력
  const onChangeOTPNumber = event => {
    const otpNumbers = event.target.value?.replace(/[^0-9]/g, '');

    if (otpNumbers?.length > 6) {
      return;
    }
    setQRData({ ...QRData, otpSerialNumber: otpNumbers });
  };

  // 다음 버튼 활성화 검증
  const checkIsDisabledBtn = () => {
    return isNewQR && (!qrFileSeq || otpSerialNumber?.length !== 6);
  };

  // 다음 버튼 클릭
  const onClickNextBtn = () => {
    // 기존 저장된 QR 데이터 존재
    if (formData.qrFileUrl) {
      setStep('managerStep');
    } else {
      setModalData({
        contents: `해당 계정에 등록된 QR코드 이미지가 없습니다. 'QR코드 등록'을 눌러 등록해 주세요.`,
        confirmBtnName: 'QR코드 등록',
        cancelBtnName: '취소',
        onClickConfirmBtn: () => {
          setIsNewQR(true);
          setShowModal(false);
        },
      });
      setShowModal(true);
    }
  };

  // 메인 타이틀 및 설명 생성
  const makeTitleDescription = () => {
    return (
      <p className="title">
        {isDirectQRStep ? 'OTP인증을 진행해 주세요' : 'OTP인증이 발생했어요'}
        <br />
        {isNewQR ? (
          <span>
            OTP인증을 위해 QR코드를 업로드해 주세요
            <FeatherIcon
              style={{
                marginLeft: '5px',
                marginTop: '2px',
                cursor: 'pointer',
              }}
              icon="alert-circle"
              size={15}
              stroke="#78797c"
              onClick={() => {
                setShowModal(true);
                setModalData({
                  contents: <QRGuideModalContents />,
                });
              }}
            />
          </span>
        ) : isDirectQRStep ? (
          <span>
            기존에 등록된 QR코드로 OTP인증이 실패했어요.
            <br />
            변경된 QR코드를 등록해 주세요.
          </span>
        ) : (
          <span>
            기존에 등록된 QR코드가 있다면 '다음' 버튼을 눌러 진행해 주세요
          </span>
        )}
      </p>
    );
  };

  // QR 유효성 검증 api
  const fetchValidateQR = async () => {
    try {
      const res = await requestApi({
        url: '/v1/au/data-provider-mall/interlock/mall-auth-qr-validate',
        method: 'POST',
        data: {
          dataProviderSeq: dataProviderSeq,
          mallSeq: mallSeq,
          qrFileSeq: qrFileSeq,
          otpSerialNumber: Number(otpSerialNumber),
        },
      });

      if (res.success) {
        // 성공하면 반환되는 key를 setFormData로 추가
        setFormData({
          ...formData,
          qrFileSeq: qrFileSeq,
          secondAuthType: 'SAT132',
          secondAuthValue: res?.data?.result.secondAuthValue,
        });
        // 담당자 지정 단계로 이동
        setStep('managerStep');
      } else {
        setShowModal(true);
        setModalData({
          contents: res.message,
        });
      }
    } catch (error) {
      setShowModal(true);
      setModalData({
        contents: COMMON_ERR_MESSAGE,
      });
    }
  };

  // 이미지 업로드
  const fetchUploadFile = async file => {
    setContextLoading(true);
    try {
      const fileFormData = new FormData();
      fileFormData.append(`imageFileList`, file);

      const res = await requestApi({
        url: '/v1/au/file/upload-image-private-list',
        method: 'POST',
        data: fileFormData,
      });
      const { fileUrl, fileSeq } = res.data?.fileList[0] || {};
      setQRData({ ...QRData, qrFileUrl: fileUrl, qrFileSeq: fileSeq });
    } catch (error) {
      setShowModal(true);
      setModalData({
        contents: COMMON_ERR_MESSAGE,
      });
    } finally {
      setContextLoading(false);
    }
  };

  // 모달 컨텐츠 - QR 이미지 업로드 방법
  const QRGuideModalContents = () => {
    return (
      <dl>
        <dt style={{ marginBottom: '15px' }}>
          <strong>QR코드 이미지 업로드 방법</strong>
        </dt>
        <dd>
          <ol style={{ padding: '0 5px' }}>
            <li>
              무신사 파트너에 접속한 후, 내정보> '2차인증(QR CODE)'을 선택해
              주세요.
            </li>
            <li>화면에 표시된 QR코드만 포함되도록 캡처해 주세요.</li>
            <li>캡처한 QR코드 이미지를 업로드 해주세요.</li>
          </ol>
        </dd>
      </dl>
    );
  };

  // 모달 컨텐츠 - 인증번호 입력 방법
  const OTPGuideModalContents = () => {
    return (
      <dl>
        <dt style={{ marginBottom: '15px' }}>
          <strong>인증번호 입력 방법</strong>
        </dt>
        <dd>
          <ol style={{ padding: '0 5px' }}>
            <li>
              앱스토어 또는 구글플레이에서 'Google Authenticator' 앱을 다운받아
              주세요.
            </li>
            <li>앱에서 계정을 추가한 후, [QR코드 스캔]을 선택해 주세요.</li>
            <li>
              무신사에서 제공한 QR코드를 스캔한 후, 표시된 6자리 OTP 인증번호를
              입력해 주세요.
            </li>
          </ol>
        </dd>
      </dl>
    );
  };

  return (
    <MainWrapper>
      {!isDirectQRStep && <GoBackToPrev onClickReset={onClickBackBtn} />}
      <div className="contentsWrapper">
        {makeTitleDescription()}
        {isNewQR ? (
          // 등록 모드로 진입한 경우, 'QR코드 재등록', 'QR코드 등록' 버튼 클릭
          <section className="QRFormStepRegisterBox">
            {qrFileUrl && (
              <div className="imgBox">
                <img alt="무신사 QR" src={qrFileUrl} />
              </div>
            )}
            <label className="uploadQRImg">
              QR코드 이미지 업로드
              <input
                type="file"
                accept="image/png, image/jpg, image/jpeg"
                className="hidden"
                onChange={onChangeImageFile}
              />
            </label>
            {qrFileUrl && (
              <div className="enterOtpNumber">
                <label>
                  OTP 인증번호를 입력해 주세요
                  <FeatherIcon
                    style={{
                      marginLeft: '5px',
                      cursor: 'pointer',
                    }}
                    icon="alert-circle"
                    size={15}
                    stroke="#78797c"
                    onClick={() => {
                      setShowModal(true);
                      setModalData({
                        contents: <OTPGuideModalContents />,
                      });
                    }}
                  />
                </label>
                <input
                  type="text"
                  placeholder="6자리 숫자를 입력해 주세요."
                  onChange={onChangeOTPNumber}
                  value={otpSerialNumber}
                />
              </div>
            )}
            <div className="nextBtnBox">
              <StyledActiveButton
                onClick={fetchValidateQR}
                disabled={checkIsDisabledBtn()}
              >
                다음
              </StyledActiveButton>
            </div>
          </section>
        ) : isDirectQRStep ? (
          // 연동 목록에서 진입한 경우 (isDirectQRStep)
          <section>
            <div className="nextBtnBox">
              <StyledActiveButton
                onClick={() => {
                  setIsNewQR(true);
                }}
              >
                QR코드 재등록
              </StyledActiveButton>
            </div>
          </section>
        ) : (
          // 수정 모드로 진입한 경우
          <section className="QRFormStepPassBox">
            <p>
              * QR코드가 변경되었거나 새로 등록이 필요한 경우,
              <br />
              <button
                type="button"
                onClick={() => {
                  setIsNewQR(true);
                  // QR 데이터 기존값 삭제
                  setFormData({
                    ...formData,
                    qrFileUrl: '',
                    qrFileSeq: 0,
                    secondAuthValue: '',
                  });
                }}
              >
                'QR코드 등록'
              </button>
              을 눌러 등록해 주세요.
            </p>
            <div className="nextBtnBox">
              <StyledActiveButton
                onClick={onClickNextBtn}
                disabled={checkIsDisabledBtn()}
              >
                다음
              </StyledActiveButton>
            </div>
          </section>
        )}
      </div>
    </MainWrapper>
  );
};

export default FillQRFormStep;
